import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { CreditCard, Landmark, Save, CheckCircle } from 'lucide-react';
import PageHeader from '../common/PageHeader';

// 💳 결제 관리 (결제등록 기획 P1) — 역할별 섹션
// 국내 기공소: 글로벌 수취 정보(SWIFT·Wise·PayPal) → 발행 인보이스에 스냅샷
// 해외 기공소: Billing profile (영문) → 인보이스 Bill To + 선호 결제수단
// 치과: P2(CMS·카드) 안내
function PaymentSettings({ user }) {
    const uid = user?.companyId || user?.uid;
    const isOverseas = user?.labKind === 'overseas';
    const isLab = user?.businessType === 'lab' && !isOverseas;
    const [form, setForm] = useState({
        bankName: '', bic: '', accountNo: '', beneficiary: '', bankAddress: '',
        wiseUrl: '', paypalMe: '',
        legalName: '', address: '', country: '', taxId: '', invoiceEmail: '', preferredMethod: 'bank',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        (async () => {
            if (!uid) return;
            try {
                const snap = await getDoc(doc(db, 'paymentProfiles', uid));
                if (snap.exists()) {
                    const d = snap.data();
                    setForm(prev => ({
                        ...prev,
                        ...(d.global || {}),
                        ...(d.billing || {}),
                    }));
                } else if (isOverseas) {
                    setForm(prev => ({
                        ...prev,
                        legalName: user?.businessName || '',
                        country: user?.country || '',
                        invoiceEmail: user?.email || '',
                    }));
                }
            } catch (e) { console.error('결제 프로필 로드 실패:', e); }
            finally { setLoading(false); }
        })();
        // eslint-disable-next-line
    }, [uid]);

    const set = (k) => (e) => { setForm(prev => ({ ...prev, [k]: e.target.value })); setSaved(false); };

    const save = async () => {
        setSaving(true);
        try {
            const payload = isOverseas
                ? {
                    role: 'overseas',
                    billing: {
                        legalName: form.legalName.trim(), address: form.address.trim(), country: form.country.trim(),
                        taxId: form.taxId.trim(), invoiceEmail: form.invoiceEmail.trim(), preferredMethod: form.preferredMethod,
                    },
                    updatedAt: new Date(),
                }
                : {
                    role: 'lab',
                    global: {
                        bankName: form.bankName.trim(), bic: form.bic.trim(), accountNo: form.accountNo.trim(),
                        beneficiary: form.beneficiary.trim(), bankAddress: form.bankAddress.trim(),
                        wiseUrl: form.wiseUrl.trim(), paypalMe: form.paypalMe.trim(),
                    },
                    updatedAt: new Date(),
                };
            await setDoc(doc(db, 'paymentProfiles', uid), payload, { merge: true });
            // 해외 Billing은 국내 기공소가 인보이스 Bill To에 써야 하므로 users에도 요약 공개
            if (isOverseas) {
                try {
                    await updateDoc(doc(db, 'users', user.uid), {
                        billingSummary: { legalName: form.legalName.trim(), country: form.country.trim(), preferredMethod: form.preferredMethod },
                    });
                } catch (e) { /* 무시 */ }
            }
            setSaved(true);
        } catch (e) {
            console.error('결제 프로필 저장 실패:', e);
            alert(isOverseas ? 'Failed to save. Please try again.' : '저장에 실패했습니다.');
        } finally { setSaving(false); }
    };

    if (loading) return <div style={{ padding: 40, color: '#5A6072' }}>{isOverseas ? 'Loading…' : '불러오는 중…'}</div>;

    // ── 치과: P2 안내 ──
    if (!isLab && !isOverseas) {
        return (
            <div style={S.wrap}>
                <PageHeader icon={CreditCard} title="결제 관리" />
                <div style={S.card}>
                    <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6 }}>자동결제 등록 준비 중</div>
                    <p style={{ fontSize: 13.5, color: '#5A6072', margin: 0, lineHeight: 1.7 }}>
                        CMS 자동이체 계좌·결제 카드 등록 기능이 곧 제공됩니다.<br />
                        그 전까지 기공소 정산은 기존 방식(거래명세서 확인 후 이체)으로 진행해주세요.
                    </p>
                </div>
            </div>
        );
    }

    // ── 해외: Billing profile (영문) ──
    if (isOverseas) {
        return (
            <div style={S.wrap}>
                <PageHeader icon={CreditCard} title="Billing" />
                <div style={S.card}>
                    <div style={S.secTitle}><Landmark size={16} /> Billing profile</div>
                    <p style={S.hint}>Printed on your invoices (Bill To) and shown to your Korean lab.</p>
                    <label style={S.label}>Company legal name *</label>
                    <input style={S.input} value={form.legalName} onChange={set('legalName')} />
                    <label style={S.label}>Billing address</label>
                    <input style={S.input} value={form.address} onChange={set('address')} placeholder="Street, city, state/province, ZIP" />
                    <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ flex: 1 }}>
                            <label style={S.label}>Country</label>
                            <input style={S.input} value={form.country} onChange={set('country')} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={S.label}>Tax ID (optional)</label>
                            <input style={S.input} value={form.taxId} onChange={set('taxId')} placeholder="VAT / EIN…" />
                        </div>
                    </div>
                    <label style={S.label}>Invoice email</label>
                    <input style={S.input} value={form.invoiceEmail} onChange={set('invoiceEmail')} />
                    <label style={S.label}>Preferred payment method</label>
                    <select style={S.input} value={form.preferredMethod} onChange={set('preferredMethod')}>
                        <option value="bank">Bank transfer (SWIFT)</option>
                        <option value="wise">Wise</option>
                        <option value="paypal">PayPal</option>
                    </select>
                    <button style={S.saveBtn} onClick={save} disabled={saving}>
                        {saved ? <><CheckCircle size={15} /> Saved</> : <><Save size={15} /> {saving ? 'Saving…' : 'Save'}</>}
                    </button>
                </div>
            </div>
        );
    }

    // ── 국내 기공소: 글로벌 수취 정보 ──
    return (
        <div style={S.wrap}>
            <PageHeader icon={CreditCard} title="결제 관리" />
            <div style={S.card}>
                <div style={S.secTitle}><Landmark size={16} /> 글로벌 수취 정보 (해외 인보이스용)</div>
                <p style={S.hint}>
                    해외 거래처에 발행하는 영문 인보이스의 <b>Payment instructions</b>에 그대로 표시됩니다.
                    발행 시점의 정보가 인보이스에 고정 저장되므로, 변경해도 이미 발행한 인보이스는 바뀌지 않습니다.
                </p>
                <div style={S.subTitle}>은행 송금 (SWIFT)</div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                        <label style={S.label}>Bank name (영문)</label>
                        <input style={S.input} value={form.bankName} onChange={set('bankName')} placeholder="KOOKMIN BANK" />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={S.label}>SWIFT / BIC</label>
                        <input style={S.input} value={form.bic} onChange={set('bic')} placeholder="CZNBKRSE" />
                    </div>
                </div>
                <label style={S.label}>Account number</label>
                <input style={S.input} value={form.accountNo} onChange={set('accountNo')} />
                <label style={S.label}>Beneficiary (수취인 영문명)</label>
                <input style={S.input} value={form.beneficiary} onChange={set('beneficiary')} placeholder="IVAN DENTAL LAB CO." />
                <label style={S.label}>Bank address (영문, 선택)</label>
                <input style={S.input} value={form.bankAddress} onChange={set('bankAddress')} />

                <div style={S.subTitle}>간편 송금 링크 (선택)</div>
                <label style={S.label}>Wise 결제 링크</label>
                <input style={S.input} value={form.wiseUrl} onChange={set('wiseUrl')} placeholder="https://wise.com/pay/..." />
                <label style={S.label}>PayPal.me 링크</label>
                <input style={S.input} value={form.paypalMe} onChange={set('paypalMe')} placeholder="https://paypal.me/..." />

                <button style={S.saveBtn} onClick={save} disabled={saving}>
                    {saved ? <><CheckCircle size={15} /> 저장됨</> : <><Save size={15} /> {saving ? '저장 중…' : '저장'}</>}
                </button>
            </div>
        </div>
    );
}

const S = {
    wrap: { maxWidth: 640, margin: '0 auto', padding: 20 },
    card: { background: '#fff', border: '1px solid #E4E6EE', borderRadius: 14, padding: '22px 24px', marginTop: 14 },
    secTitle: { display: 'flex', alignItems: 'center', gap: 7, fontSize: 15.5, fontWeight: 800, color: '#1E2230' },
    subTitle: { fontSize: 13, fontWeight: 800, color: '#334155', margin: '18px 0 2px' },
    hint: { fontSize: 12.5, color: '#94A3B8', margin: '6px 0 8px', lineHeight: 1.6 },
    label: { display: 'block', fontSize: 12, fontWeight: 700, color: '#5A6072', margin: '10px 0 4px' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #E4E6EE', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: '#fff' },
    saveBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 18, padding: '11px 22px', background: '#4F52C7', color: '#fff', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
};

export default PaymentSettings;
